import classNames from 'classnames';
import React, { useRef, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import MotifTooltipOverlay from './tooltip-overlay/tooltip-overlay';
import MotifTooltipContent from './tooltip-content/tooltip-content';
import placementOptions from './placementOptions';

import { MotifIconButton } from '../button/icon-button/icon-button';
import { ReactComponent as HardwareIcClose24px } from '../../assets/icons/navigation/ic_close_24px.svg';
import { canHandleOutside } from '../../utils/click-outside.util';

// Turn on flag to show tooltip
const SHOW_TOOLTIP = 'SHOW_TOOLTIP';

// Turn off flag to show tooltip
const HIDE_TOOLTIP = 'HIDE_TOOLTIP';

// Turn on trigger flag clicked/selected/focused
const SET_FOCUS = 'SET_FOCUS';

// Turn off flag to show tooltip and trigger flag clicked/selected/focused
const HIDE_BLUR = 'HIDE_BLUR';

// Turn on flag to show tooltip and trigger flag clicked/selected/focused
const SHOW_FOCUS = 'SHOW_FOCUS';

// Reducer initial state
const initReducerState = {
  show: false,
  focus: false,
  clicked: false
};

const reducer = (state, action) => {
  const { focus, show, clicked: oldClicked } = state;
  const { type, clicked: newClicked = oldClicked } = action;
  switch (type) {
    case SHOW_TOOLTIP:
      return { focus, show: true, clicked: newClicked };
    case HIDE_TOOLTIP:
      return { focus, show: false, clicked: newClicked };
    case SET_FOCUS:
      return { focus: true, show, clicked: newClicked };
    case HIDE_BLUR:
      return { focus: false, show: false, clicked: newClicked };
    case SHOW_FOCUS:
      return { focus: true, show: true, clicked: newClicked };
    default:
      return state;
  }
};

const defaultIcons = {
  close: <HardwareIcClose24px />
};

/**
 * @typedef Props
 * @prop {React.ReactNode} children
 * @prop {string | number} [id]
 * @prop {'default' | 'alt'} [variant]
 * @prop {React.ReactNode} [trigger]
 * @prop {'auto' | 'top' | 'right' | 'bottom' | 'left'} [placement]
 * @prop {string} [contentClassName]
 * @prop {boolean} [hide]
 * @prop {boolean} [flip]
 * @prop {{}} [closeButtonProps]
 * @prop {boolean} [allowClick]
 * @prop {boolean} [allowHover]
 * @prop {number} [forceHideCounter]
 * @prop {boolean} [hideCloseButton]
 * @prop {boolean} [hideArrow]
 * @prop {{close: React.ReactNode}} [icons]
 * @prop {{}} [style]
 */
/**
 * @param {Props} props
 */
const MotifTooltip = (props) => {
  const {
    id = 'tooltip',
    variant = 'default',
    trigger = null,
    placement = 'auto',
    children,
    contentClassName = '',
    hide = false,
    flip = true,
    closeButtonProps = {},
    allowClick = true,
    allowHover = true,
    forceHideCounter = 0,
    hideCloseButton = false,
    hideArrow = false,
    icons: userIcons = {},
    style = {}
  } = props;
  const icons = {
    ...defaultIcons,
    ...userIcons
  };
  const target = useRef(null);
  const content = useRef(null);
  const [state, dispatch] = useReducer(reducer, initReducerState);

  useEffect(() => {
    window.addEventListener('click', clickOutside);

    return () => window.removeEventListener('click', clickOutside);
  }, []);

  // hide the tooltip anytime 'forceHideCounter' prop is updated
  useEffect(() => {
    dispatch({ type: HIDE_TOOLTIP, clicked: false });
  }, [forceHideCounter]);

  // Executed on any window click event, hides tooltip and removes clicked flag
  // if click is outside this component
  const clickOutside = event => {
    if (canHandleOutside(content, event) && canHandleOutside(target, event)) {
      dispatch({ type: HIDE_BLUR, clicked: false });
    }
  };

  // Handle trigger click
  const handleTriggerClick = () => {
    // Proceed only if allowClick=TRUE
    if (!allowClick) {
      return;
    }
    // Toggle clicked flag
    const clicked = !state.clicked;
    if (state.focus) {
      // trigger already has focus
      if (!hide) {
        dispatch({ type: SHOW_TOOLTIP, clicked });
      }
    } else if (hide) {
      // trigger not focused but prop hide=TRUE, only set focus
      dispatch({ type: SET_FOCUS, clicked });
    } else {
      // trigger not focused and prop hide=FALSE, set focus and show tooltip
      dispatch({ type: SHOW_FOCUS, clicked });
    }
  };

  // Handle click on tooltip close button
  const handleCloseClick = () => dispatch({ type: HIDE_TOOLTIP, clicked: false });

  // Handle trigger focus (clicking on it or using keyboard)
  const handleFocus = () => {
    if (allowClick) {
      if (hide) {
        dispatch({ type: SET_FOCUS });
      } else {
        dispatch({ type: SHOW_FOCUS });
      }
    }
  };

  // Handle when trigger loses focus (clicking outside or through keyboard)
  const handleBlur = () => {
    if (!state.clicked) {
      dispatch({ type: HIDE_BLUR, clicked: false });
    }
  };

  // Handle pressing ESCAPE key
  const handleKeyDown = event => {
    if (event.key === 'Escape') {
      dispatch({ type: HIDE_TOOLTIP });
    }
  };

  // Handle trigger mouse over
  const handleMouseEnter = () => {
    if (allowHover && !hide) {
      dispatch({ type: SHOW_TOOLTIP });
    }
  };

  // Handle trigger mouse out
  const handleMouseLeave = () => {
    // hide tooltip when allowHover=TRUE and component not in focus
    if (allowHover && !state.clicked) {
      dispatch({ type: HIDE_TOOLTIP, clicked: false });
    }
  };

  // Boolean used to show tooltip. Tooltip is visible if 'hide' prop is FALSE
  // and reducer state 'show' is TRUE
  const isTooltipVisible = !hide && state.show;

  const buttonIconClose = hideCloseButton ? null : (
    <MotifIconButton
      aria-label="Click here to close tooltip"
      type="button"
      onClick={handleCloseClick}
      {...closeButtonProps}
    >
      {icons.close}
    </MotifIconButton>
  );

  const classes = classNames({
    'motif-tooltip-wrapper': true,
    'motif-tooltip-alternate': variant === 'alt'
  });

  const triggerClasses = classNames({
    'motif-tooltip-trigger-wrap': true,
    'motif-tooltip-clicked': state.clicked,
    'motif-tooltip-variant-link': trigger && trigger.props && trigger.props.variant === 'link',
    'motif-tooltip-show': isTooltipVisible,
    'motif-tooltip-alternate': variant === 'alt'
  });

  const contentClasses = classNames({
    'motif-tooltip-content': true,
    'motif-tooltip-alternate': variant === 'alt',
    'motif-tooltip-placement-left': placement === 'left',
    [contentClassName]: true
  });

  const customStyle = {
    'right': { position: 'left', size: '14px' },
    'bottom': { position: 'top', size: '2px' }
  };

  return (
    <span className={classes} data-motif-tooltip>
      <span
        className={triggerClasses}
        ref={target}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleTriggerClick}
      >
        {trigger}
      </span>
      <MotifTooltipOverlay
        target={target.current}
        show={isTooltipVisible}
        placement={placement}
        flip={flip}
      >

        {overLayProps => {
          const customProps = Object.keys(customStyle).some(key => key === overLayProps.placement) ?
          { ...overLayProps, style: overLayProps.style && { ...overLayProps.style, [customStyle[overLayProps.placement].position]: customStyle[overLayProps.placement].size } } :
          overLayProps;

          return (
            <div className={contentClasses} ref={content} style={style}>
              <MotifTooltipContent hideArrow={hideArrow} {...customProps}>
                <div role="tooltip" aria-live="polite" className="motif-tooltip-content-children">{children}</div>
                {buttonIconClose}
              </MotifTooltipContent>
            </div>
          );
        }}
      </MotifTooltipOverlay>
      </span>
  );
};

MotifTooltip.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.oneOf(['default', 'alt']),
  trigger: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType]),
  placement: PropTypes.oneOf(placementOptions),
  contentClassName: PropTypes.string,
  children: PropTypes.node,
  hide: PropTypes.bool,
  flip: PropTypes.bool,
  closeButtonProps: PropTypes.instanceOf(Object),
  style: PropTypes.instanceOf(Object),
  allowClick: PropTypes.bool,
  allowHover: PropTypes.bool,
  forceHideCounter: PropTypes.number,
  hideCloseButton: PropTypes.bool,
  hideArrow: PropTypes.bool,
  icons: PropTypes.shape({
    close: PropTypes.node
  })
};

export { MotifTooltip };
